import generateVersionHash from '@utils/generateVersionHash';
import axios from 'axios';
import { api,  uploadFailed } from './constants';
import mutations from './mutations';
import state from './state';
import { postApi } from '../../../lib/fetcher';

const dataIntervalUpload = 5000;

const insertTransactionOnUs = {
  namespaced: true,
  state,
  mutations,
  actions: {
    async downloadTemplate(ctx) {
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}${api.DOWNLOAD_TEMPLATE}`,
        responseType: 'blob',
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
            .token,
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
      };

      return axios(config)
        .then((response) => response)
        .catch(async (error) => {
          let message = 'Failed to download template. Please try again.';
          if (error.response?.data instanceof Blob) {
            try {
              const parsed = JSON.parse(await error.response.data.text());
              message =
                parsed.message || parsed.message_en || parsed.message_id || message;
            } catch (e) {
              // response body is not JSON, keep the default message
            }
          }
          if (error.response?.status === 401) {
            ctx.dispatch('logout', null, { root: true });
          } else {
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message,
                icon: 'mdi-alert',
                color: 'red',
              },
              { root: true }
            );
          }

          return null;
        });
    },

    async bulkUpload(ctx, { file, date }) {
      const data = new FormData();

      ctx.commit('setShowProgressBar', true); 
      ctx.commit('setProgressColor', 'green');
      const progressSimulation = setInterval(() => {
        if (ctx.state.percentage < 80) {
          ctx.commit('setPercentage', ctx.state.percentage + 1);
        } else {
          clearInterval(progressSimulation);
        }
      }, 100);

      data.append('file', file);
      ctx.commit('setOnUpload', true);

      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}${api.BULK_UPLOAD}`,
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
            .token,
          'Content-Type': 'multipart/form-data',
          'X-Origin-Path': window.location.pathname
        },
        onUploadProgress(progressEvent) {
          ctx.commit(
            'setUploadProgress',
            parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100),
              10
            )
          );
        },
        data,
      };

      try {
        return await axios(config)
          .then((response) => {
            if (response.data.code === 200) {
              
              ctx.commit('setShowProgressBar', true);
              ctx.commit('setImportData', response.data.data);

              const interval = setInterval(() => {
                ctx.dispatch('importStatus', {
                  date,
                  controller: 'bulk-insert-transaction-on-us',
                });
              }, dataIntervalUpload);

              ctx.commit('setIntervalImport', interval);
              ctx.commit('setOnInsert', true);
              
            } else {
              uploadFailed(ctx, progressSimulation); 
              if (response.data.code === 302) {
                ctx.commit(
                  'setSnackbar',
                  {
                    status: true,
                    message: response.data.data.message,
                    icon: 'mdi-alert',
                    color: 'red',
                  },
                  { root: true }
                );
              }
            }

            return response.data;
          })
          .catch((error) => {
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message: 'Something went wrong !',
                icon: 'mdi-alert',
                color: 'red',
              },
              { root: true }
            );
            ctx.commit('setOnUpload', false);
            clearInterval(progressSimulation);
            ctx.commit('setProgressTitle', 'Request Failed');
            ctx.commit('setProgressColor', 'red');
            ctx.commit('setPercentage', 0);
            ctx.commit('setFailedResponse', true);

            if (error.response.status === 403) {
              ctx.commit(
                'setSnackbar',
                {
                  status: true,
                  message: error.response.data.message,
                  icon: 'mdi-alert',
                  color: 'red',
                },
                { root: true }
              );
            } else if (error.response.status === 401) {
              ctx.dispatch('logout', null, { root: true });
            }

            return null;
          });
      } catch (error) {
        uploadFailed(ctx, progressSimulation);

        return false;
      }  finally {
        clearInterval(ctx.state.intervalInsert);
        ctx.commit('setIntervalInsert', null);
        setTimeout(() => {
          ctx.commit('setLoading', false);
        }, 500); 
      }
    },

    async importStatus(ctx, { date, controller }) {
      ctx.commit('setLoading', true);

      const version = generateVersionHash(ctx.state.importId.toString() + date);

      const data = JSON.stringify({
        import_id: ctx.state.importId,
        count_data: ctx.state.countData,
        version
      });

      const setCountToDefault = () => {
        ctx.commit('setStateVariable', {
          payload: 0,
          variable: 'countDone',
        });
        ctx.commit('setStateVariable', {
          payload: 0,
          variable: 'countSame',
        });
      };

      const failed = (response) => {
        ctx.commit('setProgressTitle', 'Request Failed');
        ctx.commit('setProgressColor', 'red');
        ctx.commit('setPercentage', 0);
        ctx.commit('setFailedResponse', true);

        clearInterval(ctx.state.intervalImport);
        ctx.commit('setOnInsert', false);
        ctx.commit(
          'setSnackbar',
          {
            status: true,
            message: response?.data?.message || 'Upload Failed !',
            icon: 'mdi-alert',
            color: 'red',
          },
          { root: true }
        );
        ctx.commit(
          'setInsertProgressGlobal',
          {
            absoluteBar: false,
            absoluteBarText: '',
          },
          { root: true }
        );
        setCountToDefault();
      };

      try {
        const urlApi = `/index.php?r=api/v1/transaction/${controller}/status-import`;

        const responseData = await postApi(ctx, urlApi, data);

        if (responseData.code === '00') {
          clearInterval(ctx.state.intervalImport);
          setTimeout(() => {
            ctx.commit('setPercentage', 100);
            ctx.commit('setProgressTitle', 'Request Complete');
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message: responseData?.message_id || 'Success, Bulk Upload Done!',
                icon: 'mdi-check-circle',
                color: 'green',
              },
              { root: true }
            );
          }, 1000);
          ctx.commit('setOnInsert', false);
          ctx.commit('setStateVariable', {
            variable: 'countSuccess',
            payload: responseData.data.count_success,
          });
          ctx.commit('setStateVariable', {
            variable: 'countFailed',
            payload: responseData.data.count_failed,
          });
          if (Number(responseData.data.count_success) !== Number(ctx.state.countData)) {
            ctx.commit('setStateVariable', {
              variable: 'failedImportController',
              payload: controller,
            });
            ctx.commit('setStateVariable', {
              variable: 'failedImportDate',
              payload: date,
            });
            ctx.commit('setStateVariable', {
              variable: 'hasFailedImport',
              payload: true,
            });
          }
          setCountToDefault();
          ctx.commit(
            'setInsertProgressGlobal',
            {
              absoluteBar: false,
              absoluteBarText: '',
            },
            { root: true }
          );
        } else if (responseData.code === 302) {
          failed(responseData);
        } else {
          const totalDataDone =
            parseInt(responseData.data.count_success, 10) +
            parseInt(responseData.data.count_failed, 10);

          if (ctx.state.countDone !== totalDataDone) {
            ctx.commit('setStateVariable', {
              payload: totalDataDone,
              variable: 'countDone',
            });
            ctx.commit('setStateVariable', {
              payload: 0,
              variable: 'countSame',
            });
          } else {
            ctx.commit('setStateVariable', {
              payload: ctx.state.countSame + 1,
              variable: 'countSame',
            });
          }
          if (ctx.state.countSame > 2) {
            failed(responseData);
          }
        }
      } catch (error) {
        failed();
      }
    },

    async downloadFailedImport(ctx) {
      const date = ctx.state.failedImportDate;
      const controller = ctx.state.failedImportController;

      const version = generateVersionHash(ctx.state.importId.toString() + date);
      const data = JSON.stringify({
        import_id: ctx.state.importId,
        version,
      });

      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/transaction/${controller}/download-failed-import`,
        responseType: 'blob',
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram')).token,
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data,
      };

      return axios(config)
        .then((response) => response)
        .catch(async (error) => {
          let message = 'Failed to download failed records. Please try again.';
          if (error.response?.data instanceof Blob) {
            try {
              const parsed = JSON.parse(await error.response.data.text());
              message =
                parsed.message || parsed.message_en || parsed.message_id || message;
            } catch (e) {
              // response body is not JSON, keep the default message
            }
          }
          if (error.response?.status === 401) {
            ctx.dispatch('logout', null, { root: true });
          } else {
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message,
                icon: 'mdi-alert',
                color: 'red',
              },
              { root: true }
            );
          }
          return null;
        });
    },

    closeProgressBar({ commit }) {
      commit('setShowProgressBar', false);
    },
    clearDownloadFailedURL({ commit }) {
      commit('clearFailedImport');
    }
  },
};

export default insertTransactionOnUs;
